import React from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { ConfluencePageContributor } from '../../../../../api/useTwgSources/gql/types';
import { messages } from '../../messages';
import { ContributorAvatarStack } from './ContributorAvatarStack';
import { ContributorCard } from './ContributorCard';

const DEFAULT_CONTRIBUTORS_TO_DISPLAY_WHEN_COLLAPSED = 3;
const MAX_CONTRIBUTORS_TO_DISPLAY = 15;

interface ContributorsProps {
	contributors: ConfluencePageContributor[];
	cloudId: string;
	isExpanded: boolean;
	onExpandToggle: (event: React.MouseEvent<Element, MouseEvent>) => void;
	onContributorClick: (action: string) => void;
	singleColumnLayout?: boolean;
}

const styles = cssMap({
	contributorsTitleContainer: {
		marginBottom: token('space.050'),
	},
	singleColumnLayout: {
		gridTemplateColumns: '1fr',
	},
	multiColumnLayout: {
		gridTemplateColumns: 'repeat(3, 1fr)',
	},
});

export const Contributors = ({
	contributors,
	cloudId,
	isExpanded,
	onExpandToggle,
	onContributorClick,
	singleColumnLayout = false,
}: ContributorsProps): React.JSX.Element => {
	const truncatedContributors = contributors.slice(0, MAX_CONTRIBUTORS_TO_DISPLAY);
	const contributorsToDisplayWhenCollapsed = singleColumnLayout
		? 1
		: DEFAULT_CONTRIBUTORS_TO_DISPLAY_WHEN_COLLAPSED;

	const contributorsToDisplay = isExpanded
		? truncatedContributors
		: truncatedContributors.slice(0, contributorsToDisplayWhenCollapsed);

	return (
		<>
			<Inline spread="space-between" alignBlock="center" xcss={styles.contributorsTitleContainer}>
				<Text color="color.text.subtle" weight="medium">
					<FormattedMessage {...messages.contributors_title} />
				</Text>
				{truncatedContributors.length > contributorsToDisplayWhenCollapsed && (
					<ContributorAvatarStack
						contributors={truncatedContributors}
						onClick={onExpandToggle}
						singleColumnLayout={singleColumnLayout}
					/>
				)}
			</Inline>
			<Grid
				rowGap="space.200"
				columnGap="space.400"
				xcss={cx(singleColumnLayout ? styles.singleColumnLayout : styles.multiColumnLayout)}
			>
				{contributorsToDisplay?.map((contributor: ConfluencePageContributor) => (
					<ContributorCard
						contributor={contributor}
						key={contributor.data.accountId}
						cloudId={cloudId}
						onContributorClick={onContributorClick}
					/>
				))}
			</Grid>
		</>
	);
};
